import React from "react";
import { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from "@mui/material";
import { IconButton } from "@mui/material";
import { Button, Box, Paper, CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Tooltip } from "@mui/material";
import CreateIcon from "@mui/icons-material/Create";
import {
  ApiGet,
  ApiPost,
  ApiPut,
  ApiDelete,
} from "../helpers/API/ApiData";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast, ToastContainer } from "react-toastify";

import { DataTable, TableColumnDef } from "../components/DataTable";
import { TableThumbnail } from "../components/TableThumbnail";
import { createSanitizedMarkup } from "../utils/createSanitizedMarkup";
import { PageHeader } from "../components/PageHeader";
import { usePermissions } from "../hooks/usePermissions";
import { useImageCrop } from "../hooks";
import { useContentLanguage } from "../hooks";
import { LanguageTabs } from "../components/LanguageTabs";
import { GuidesFormDialog } from "./GuidesFormDialog";

interface EditorState {
  content: string;
  contentGer: string;
}

export default function Guides() {
  const { canCreate, canEdit, canDelete } = usePermissions("/guides");
  const [Guides, setGuides] = useState<any>() as any;
  const [addGuides, setAddGuides] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const [getAllGuides, setGetAllGuides] = useState<any[]>([]);
  const [isEditApi, setIsEditApi] = useState(false);
  const [search, setSearch] = useState<any>();
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [GuidesIdForEdit, setGuidesIdForEdit] = useState<any>();
  const [GuidesIdForDelete, setGuidesIdForDelete] = useState<any>();
  const [countPerPage, setCountPerPage] = useState(10);
  const [loader, setLoader] = useState(false);
  const [loaderForGetAll, setLoaderForGetAll] = useState(false);
  const [show, setShow] = useState(false);

  const [editorState, setEditorState] = useState<EditorState>({
    content: "",
    contentGer: "",
  });

  const [lang, setLang] = useContentLanguage();

  const imageCropEn = useImageCrop();
  const imageCropGer = useImageCrop();
  const [removeImageEn, setRemoveImageEn] = useState(false);
  const [removeImageGer, setRemoveImageGer] = useState(false);

  useEffect(() => {
    getAllGuidesData();
  }, [page, countPerPage, search]);

  const addChallengesModelClose = () => {
    setAddGuides(false);
    setIsEditApi(false);
    setErrors({});
    setGuides({});
    setEditorState({ content: "", contentGer: "" });
    imageCropEn.reset();
    imageCropGer.reset();
    setRemoveImageEn(false);
    setRemoveImageGer(false);
  };

  const onhandleChange = (e: any) => {
    const { name, value } = e.target;
    setGuides({ ...Guides, [name]: value });
    setErrors({ ...errors, [name]: "" });
  };

  const formValidation = () => {
    let formIsValid = true;
    const err: any = {};

    if (!Guides?.title?.trim() || Guides?.title === "") {
      formIsValid = false;
      err["title"] = "*Bitte geben Sie den Titel ein";
    }
    if (!Guides?.title_ger?.trim() || !Guides?.title_ger) {
      formIsValid = false;
      err["title_ger"] = "*Bitte geben Sie den Titel ein";
    }
    if (!Guides?.subTitle?.trim() || Guides?.subTitle === "") {
      formIsValid = false;
      err["subTitle"] = "*Bitte geben Sie den Untertitel ein";
    }
    if (!Guides?.subTitle_ger?.trim() || !Guides?.subTitle_ger) {
      formIsValid = false;
      err["subTitle_ger"] = "*Bitte geben Sie den Untertitel ein";
    }
    if (!imageCropEn.imageToUpload && !Guides?.image) {
      formIsValid = false;
      err["image"] = "*Bitte wählen Sie ein Bild aus";
    }
    if (!imageCropGer.imageToUpload && !Guides?.image_ger) {
      formIsValid = false;
      err["image_ger"] = "*Bitte wählen Sie ein Bild aus";
    }

    setErrors(err);
    return formIsValid;
  };

  const handleClose = () => {
    setShow(false);
  };

  const getAllGuidesData = async () => {
    setLoaderForGetAll(true);
    const params = `page=${page}&limit=${countPerPage}${search ? `&letter=${search}` : ""}`;
    await ApiGet(`guides/findGuides?${params}`)
      .then((res: any) => {
        setGetAllGuides(res?.data?.data);
        setCount(res?.data?.count ?? 0);
        setLoaderForGetAll(false);
      })
      .catch(() => {
        setLoaderForGetAll(false);
      });
  };

  const addCategoryData = async () => {
    setIsEditApi(false);
    if (formValidation()) {
      setLoader(true);
      try {
        const formData = new FormData();
        formData.append("title", Guides?.title);
        formData.append("title_ger", Guides?.title_ger);
        formData.append("subTitle", Guides?.subTitle);
        formData.append("subTitle_ger", Guides?.subTitle_ger);
        formData.append("desc", editorState?.content);
        formData.append("desc_ger", editorState?.contentGer);

        const croppedEn = await imageCropEn.getCroppedFile();
        if (croppedEn) formData.append("image", croppedEn);
        const croppedGer = await imageCropGer.getCroppedFile();
        if (croppedGer) formData.append("image_ger", croppedGer);

        await ApiPost(`guides/createGuides`, formData);
        toast.success("Erfolgreich hinzufügen");
        setAddGuides(false);
        setIsEditApi(false);
        getAllGuidesData();
        setGuides({});
        setEditorState({ content: "", contentGer: "" });
        imageCropEn.reset();
        imageCropGer.reset();
      } catch (err: any) {
        toast.error(err?.response?.data?.message || "Etwas ist schief gelaufen...");
      } finally {
        setLoader(false);
      }
    }
  };

  const EditCategoryData = async () => {
    if (formValidation()) {
      setLoader(true);
      try {
        const formData = new FormData();
        formData.append("title", Guides?.title);
        formData.append("title_ger", Guides?.title_ger);
        formData.append("subTitle", Guides?.subTitle);
        formData.append("subTitle_ger", Guides?.subTitle_ger);
        formData.append("desc", editorState?.content || Guides?.desc);
        formData.append("desc_ger", editorState?.contentGer || Guides?.desc_ger);

        const croppedEn = await imageCropEn.getCroppedFile();
        if (croppedEn) formData.append("image", croppedEn);
        const croppedGer = await imageCropGer.getCroppedFile();
        if (croppedGer) formData.append("image_ger", croppedGer);
        if (removeImageEn) formData.append("removeImage", "true");
        if (removeImageGer) formData.append("removeImage_ger", "true");

        await ApiPut(`guides/updateGuides?id=${GuidesIdForEdit}`, formData);
        toast.success("Erfolgreich geupdated");
        getAllGuidesData();
        setAddGuides(false);
        setIsEditApi(false);
        setGuides({});
        setEditorState({ content: "", contentGer: "" });
        imageCropEn.reset();
        imageCropGer.reset();
        setRemoveImageEn(false);
        setRemoveImageGer(false);
      } catch (err: any) {
        toast.error(err?.response?.data?.message || "Etwas ist schief gelaufen...");
      } finally {
        setLoader(false);
      }
    }
  };

  const DeleteCategoryData = async () => {
    await ApiDelete(`guides/deleteGuides?id=${GuidesIdForDelete}`)
      .then((res: any) => {
        if (res?.status === 200) {
          toast.success("Erfolgreich löschen");
          setShow(false);
          setPage(1);
          getAllGuidesData();
        } else {
          toast.error(res?.data?.message || "Etwas ist schief gelaufen...");
        }
      })
      .catch((err: any) => {
        toast.error(err?.response?.data?.message || "Etwas ist schief gelaufen...");
      });
  };

  const guideColumns: TableColumnDef[] = [
    {
      header: "Titel",
      render: (row: any) => (lang === "de" ? row?.title_ger : row?.title) || "-",
      getValue: (row: any) => lang === "de" ? row?.title_ger : row?.title,
    },
    {
      header: "Untertitel",
      render: (row: any) => (lang === "de" ? row?.subTitle_ger : row?.subTitle) || "-",
      getValue: (row: any) => lang === "de" ? row?.subTitle_ger : row?.subTitle,
    },
    {
      header: "Bild",
      width: "130px",
      render: (row: any) => (
        <TableThumbnail src={lang === "de" ? row?.image_ger : row?.image} />
      ),
    },
    {
      header: "Beschreibung",
      render: (row: any) => (
        <div
          dangerouslySetInnerHTML={createSanitizedMarkup({ html: lang === "de" ? row?.desc_ger : row?.desc })}
        />
      ),
    },
    {
      header: "Einstellungen",
      render: (row: any) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          {canEdit && (
            <Tooltip title="Bearbeiten" arrow>
              <IconButton
                size="small"
                color="primary"
                onClick={() => {
                  setIsEditApi(true);
                  setAddGuides(true);
                  setGuidesIdForEdit(row?._id);
                  setGuides({
                    title: row?.title,
                    title_ger: row?.title_ger,
                    subTitle: row?.subTitle,
                    subTitle_ger: row?.subTitle_ger,
                    image: row?.image,
                    image_ger: row?.image_ger,
                    desc: row?.desc,
                    desc_ger: row?.desc_ger,
                  });
                  imageCropEn.reset();
                  imageCropGer.reset();
                  setEditorState({
                    content: row?.desc || "",
                    contentGer: row?.desc_ger || "",
                  });
                }}
              >
                <CreateIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip title="Löschen" arrow>
              <IconButton
                size="small"
                color="error"
                onClick={() => {
                  setGuidesIdForDelete(row?._id);
                  setShow(true);
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  return (
    <>
      <Paper sx={{ p: 1 }}>
        <ToastContainer />

        <PageHeader
          title="Guides"
          onSearch={(val) => { setSearch(val); setPage(1); }}
          onAdd={canCreate ? () => {
            setIsEditApi(false);
            setGuides({});
            setErrors({});
            setEditorState({ content: "", contentGer: "" });
            imageCropEn.reset();
            imageCropGer.reset();
            setAddGuides(true);
          } : undefined}
        />
        <LanguageTabs value={lang} onChange={setLang} />
        {loaderForGetAll ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2.5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataTable
            rows={getAllGuides || []}
            columns={guideColumns}
            page={page}
            count={count}
            rowsPerPage={countPerPage}
            onPageChange={setPage}
            onRowsPerPageChange={setCountPerPage}
          />
        )}
      </Paper>
      <Dialog open={show} onClose={handleClose}>
        <DialogTitle sx={{ color: "error.main", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Löschen bestätigen
          <IconButton size="small" onClick={handleClose} aria-label="close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Möchtest du diese Herausforderungen wirklich löschen?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="inherit">
            Nein
          </Button>
          <Button onClick={() => DeleteCategoryData()} color="error" variant="contained">
            Ja
          </Button>
        </DialogActions>
      </Dialog>

      {addGuides && (
        <GuidesFormDialog
          open={addGuides}
          isEditApi={isEditApi}
          loader={loader}
          Guides={Guides}
          errors={errors}
          editorState={editorState}
          imageCropEn={imageCropEn}
          imageCropGer={imageCropGer}
          onClose={addChallengesModelClose}
          onhandleChange={onhandleChange}
          setEditorState={setEditorState}
          onSave={() => {
            isEditApi ? EditCategoryData() : addCategoryData();
          }}
          onRemoveImageEn={() => {
            setGuides({ ...Guides, image: "" });
            setRemoveImageEn(true);
          }}
          onRemoveImageGer={() => {
            setGuides({ ...Guides, image_ger: "" });
            setRemoveImageGer(true);
          }}
        />
      )}
    </>
  );
}
